import { useState, useEffect } from "react";
import { supabase, deleteImage, type HeroSlide } from "@/lib/supabase";
import ImageUploadField from "@/components/base/ImageUploadField";

export default function HeroSlidesManager() {
  const [slideList, setSlideList] = useState<HeroSlide[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [originalImageUrl, setOriginalImageUrl] = useState<string>("");
  const [isLoading, setIsLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({ title: "", subtitle: "", image_url: "", cta_text: "", cta_link: "", display_order: 1 });

  useEffect(() => { fetchSlides(); }, []);

  const fetchSlides = async () => {
    setIsLoading(true);
    const { data } = await supabase.from("hero_slides").select("*").order("display_order", { ascending: true });
    setSlideList(data || []);
    setIsLoading(false);
  };

  const handleEdit = (slide: HeroSlide) => {
    setFormData({ title: slide.title, subtitle: slide.subtitle, image_url: slide.image_url, cta_text: slide.cta_text, cta_link: slide.cta_link, display_order: slide.display_order });
    setOriginalImageUrl(slide.image_url);
    setEditingId(slide.id);
    setShowModal(true);
  };

  const handleSave = async () => {
    setSaving(true);
    if (editingId) {
      const { error } = await supabase.from("hero_slides").update(formData).eq("id", editingId);
      if (!error) {
        if (originalImageUrl && originalImageUrl !== formData.image_url) {
          await deleteImage(originalImageUrl);
        }
        await fetchSlides();
      }
    } else {
      const { error } = await supabase.from("hero_slides").insert(formData);
      if (!error) await fetchSlides();
    }
    setSaving(false);
    setShowModal(false);
    setEditingId(null);
    setOriginalImageUrl("");
    setFormData({ title: "", subtitle: "", image_url: "", cta_text: "", cta_link: "", display_order: 1 });
  };

  const handleDelete = async (slide: HeroSlide) => {
    if (!confirm("Are you sure you want to delete this slide?")) return;
    const { error } = await supabase.from("hero_slides").delete().eq("id", slide.id);
    if (!error) {
      await deleteImage(slide.image_url);
      await fetchSlides();
    }
  };

  const handleMove = async (index: number, direction: "up" | "down") => {
    if (direction === "up" && index === 0) return;
    if (direction === "down" && index === slideList.length - 1) return;
    const newList = [...slideList];
    const swapIndex = direction === "up" ? index - 1 : index + 1;
    [newList[index], newList[swapIndex]] = [newList[swapIndex], newList[index]];
    const updated = newList.map((s, i) => ({ ...s, display_order: i + 1 }));
    setSlideList(updated);
    await Promise.all(updated.map((s) => supabase.from("hero_slides").update({ display_order: s.display_order }).eq("id", s.id)));
  };

  if (isLoading) {
    return (
      <div className="p-4 md:p-6 lg:p-8">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-48" />
          <div className="h-32 bg-gray-100 rounded-xl" />
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 lg:p-8">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <h1 className="font-heading font-bold text-2xl text-primary-900">Hero Slides Management</h1>
        <button onClick={() => { setEditingId(null); setOriginalImageUrl(""); setFormData({ title: "", subtitle: "", image_url: "", cta_text: "", cta_link: "", display_order: slideList.length + 1 }); setShowModal(true); }} className="inline-flex items-center gap-2 px-4 py-2.5 bg-gold-600 hover:bg-gold-700 text-white text-sm font-medium rounded-lg transition-colors whitespace-nowrap">
          <i className="ri-add-line" /> Add Slide
        </button>
      </div>

      <div className="space-y-4">
        {slideList.map((slide, index) => (
          <div key={slide.id} className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
            <div className="flex flex-col md:flex-row">
              <div className="md:w-48 lg:w-56 flex-shrink-0">
                <img src={slide.image_url} alt={slide.title} className="w-full h-32 md:h-full object-cover" />
              </div>
              <div className="flex-1 p-4 md:p-5">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="font-heading font-bold text-base md:text-lg text-primary-900 mb-1">{slide.title}</p>
                    <p className="text-gray-500 text-sm line-clamp-2 mb-3">{slide.subtitle}</p>
                    <div className="flex items-center gap-3 text-xs text-gray-400">
                      <span className="flex items-center gap-1"><i className="ri-link" />{slide.cta_text} &rarr; {slide.cta_link}</span>
                      <span className="px-2 py-0.5 bg-gray-100 rounded-full">Order: {slide.display_order}</span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="px-4 md:px-5 pb-4 md:pb-0 md:py-5 flex md:flex-col items-center gap-1 md:gap-2">
                <button onClick={() => handleMove(index, "up")} disabled={index === 0} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-500 disabled:opacity-30 transition-colors"><i className="ri-arrow-up-line text-sm" /></button>
                <button onClick={() => handleMove(index, "down")} disabled={index === slideList.length - 1} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-500 disabled:opacity-30 transition-colors"><i className="ri-arrow-down-line text-sm" /></button>
                <button onClick={() => handleEdit(slide)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-500 hover:text-primary-600 transition-colors" aria-label="Edit"><i className="ri-edit-line text-sm" /></button>
                <button onClick={() => handleDelete(slide)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-red-50 text-gray-500 hover:text-red-500 transition-colors" aria-label="Delete"><i className="ri-delete-bin-line text-sm" /></button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {slideList.length === 0 && <div className="py-12 text-center text-gray-400 text-sm bg-white rounded-xl border border-gray-100">No slides yet. Add your first hero slide.</div>}

      {showModal && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <h2 className="font-heading font-bold text-lg text-primary-900">{editingId ? "Edit Slide" : "Add Slide"}</h2>
              <button onClick={() => setShowModal(false)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-500"><i className="ri-close-line" /></button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Title</label>
                <input type="text" value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-gold-500 focus:ring-1 focus:ring-gold-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Subtitle</label>
                <textarea value={formData.subtitle} onChange={(e) => setFormData({ ...formData, subtitle: e.target.value })} rows={3} className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-gold-500 focus:ring-1 focus:ring-gold-500 resize-none" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">CTA Text</label>
                  <input type="text" value={formData.cta_text} onChange={(e) => setFormData({ ...formData, cta_text: e.target.value })} className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-gold-500 focus:ring-1 focus:ring-gold-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">CTA Link</label>
                  <input type="text" value={formData.cta_link} onChange={(e) => setFormData({ ...formData, cta_link: e.target.value })} className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-gold-500 focus:ring-1 focus:ring-gold-500" />
                </div>
              </div>
              <ImageUploadField
                value={formData.image_url}
                onChange={(url) => setFormData({ ...formData, image_url: url })}
                folder="hero"
                label="Background Image"
              />
            </div>
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100">
              <button onClick={() => setShowModal(false)} className="px-4 py-2.5 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">Cancel</button>
              <button onClick={handleSave} disabled={saving || !formData.image_url} className="px-5 py-2.5 bg-gold-600 hover:bg-gold-700 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-60">{saving ? "Saving..." : "Save Slide"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}